import { useEffect, useState } from "react";
import {
  Alert,
  Backdrop,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Icon,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
} from "@mui/material";
import axios from "axios.js";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DataTable from "theme/Tables/DataTable";
import MDButton from "theme/components/MDButton";
import MDInput from "theme/components/MDInput";

function TrainingsCard({ campaignRef, triggerRefresh, courses, campaignCourses }) {
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [tableRows, setTableRows] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [openAttachCourseialog, setOpenAttachCourseDialog] = useState(false);

  async function attachCourse() {
    if (selectedCourse === null) {
      setSeverity("error");
      setErrMsg("Select a course");
      setOpen(true);
      return;
    }

    setOpenAttachCourseDialog(false);
    setShowLoading(true);
    const json = {
      campaign: campaignRef,
      course: selectedCourse,
    };

    await axios
      .post("portal/campaigns/courses/attach.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function removeCampaignCourse(ref) {
    setShowLoading(true);
    await axios
      .get(`/portal/campaigns/courses/${ref}/delete.json`)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  const campaignCoursesTableColumns = [
    { Header: "Course", accessor: "course", align: "left" },
    { Header: "Actions", accessor: "actions", align: "right" },
  ];

  const buildCampaignCoursesRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        course: row.title,
        actions: (
          <MDBox mt={1} variant="button">
            <MDButton
              size="small"
              variant="outlined"
              color="error"
              iconOnly
              onClick={() => {
                if (window.confirm(`Remove course?`) === true) {
                  removeCampaignCourse(row.reference);
                }
              }}
            >
              <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                delete
              </Icon>
            </MDButton>
          </MDBox>
        ),
      });
      return null;
    });

    return rows;
  };

  useEffect(() => {
    const cCoursesRows = buildCampaignCoursesRows(campaignCourses);
    setTableRows(cCoursesRows);
  }, [campaignCourses]);
  return (
    <MDBox>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog open={openAttachCourseialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Attach Course</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDBox mt={2}>
              <FormControl sx={{ minWidth: 120, width: "100%" }}>
                <InputLabel>Course</InputLabel>
                <Select
                  label="Course"
                  onChange={(e) => {
                    setSelectedCourse(e.target.value);
                  }}
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                >
                  {
                    // --> Notice below changes
                    courses.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                      courses.map((record) => (
                        <MenuItem value={`${record.reference}`}>{record.title}</MenuItem>
                      ))
                  }
                </Select>
              </FormControl>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenAttachCourseDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="dark"
            variant="contained"
            onClick={() => {
              attachCourse();
            }}
          >
            Attach
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDBox mt={3}>
        <Card sx={{ height: "100%", marginTop: "0px" }}>
          <MDBox>
            <MDBox p={2}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6" fontWeight="light">
                  <b>Training</b>
                  <br />
                  <small>
                    Ensure your agents are well-trained before accessing this campaign. Go to the{" "}
                    <b>Trainings</b> menu for more information.
                  </small>
                </MDTypography>
                <MDButton
                  variant="contained"
                  color="dark"
                  iconOnly
                  onClick={() => {
                    setSelectedCourse(null);
                    setOpenAttachCourseDialog(true);
                  }}
                >
                  <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                </MDButton>
              </MDBox>
            </MDBox>
            <MDBox mb={3}>
              <DataTable
                table={{ columns: campaignCoursesTableColumns, rows: tableRows }}
                isSorted
                entriesPerPage={false}
                showTotalEntries={false}
                buttons
                noEndBorder
                canSearch={false}
              />
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
    </MDBox>
  );
}

export default TrainingsCard;
